import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { postAuthSession } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";

/**
 * Emergent Google OAuth landing page.
 * The user is redirected here as `/#session_id=…` — we exchange it for a cookie
 * session on our backend, then navigate to the intended destination.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/", { replace: true });
      return;
    }
    const session_id = decodeURIComponent(match[1]);

    (async () => {
      try {
        const data = await postAuthSession(session_id);
        setUser(data.user);
        trackEvent("auth_login_success", { email: data.user?.email });
        // Wipe the hash so a refresh doesn't reprocess
        window.history.replaceState(null, "", window.location.pathname);
        toast.success(`Welcome, ${data.user?.name || "friend"}!`);
        navigate("/favourites", { replace: true });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Sign-in failed");
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center px-6" data-testid="auth-callback">
      <div className="ink-stamp red mb-4">Signing you in…</div>
      <div className="font-headline uppercase text-2xl sm:text-3xl font-black tracking-tight">
        Verifying credentials with the archives
      </div>
      <p className="mt-4 font-body italic text-ink-muted max-w-md">
        Composing your desk — this only takes a second.
      </p>
    </div>
  );
}
